import { TokenService } from './services/token.service';
import { mergeMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { createRequestOptions } from './utils/request.utils';
import { oauthLogger } from './utils/logging.utils';
import { handleCorb, handleErrorStatus } from './utils/response.utils';
import { clearStore } from './utils/store.util';
/**
 * Verantwortlich für die Bereitstellung von Operationen bezüglich dem Versenden von Anfragen und interpretieren der Antworten,
 * sowie deren Ressourcen-Management.
 */
export class ProtectedRestService {
  /**
   * @param baConfig wenn keine BaHttpConfig bei einem Aufruf mit übergeben wird, dann wird diese Konfiguration verwendet.
   */
  constructor(baConfig) {
    this.baConfig = baConfig;
    this.service = new TokenService();
  }
  /**
   * Ermöglicht Anfragen mittels der fetch-API innerhalb des Online-Bereichs der BA unter Berücksichtigung der spezifischen (Sicherheits-) Infrastruktur.
   * @param url
   * @param init siehe RequestInit von fetch
   * @param config wenn eine gültige Konfiguration übergeben wird, wird nur diese Konfiguration verwendet
   * @throws wenn übergebene Konfiguration gleich null ist
   * @return json-geparstes Objekt als Promise<T>
   */
  doJsonFetch(url, init, config) {
    return this.fetch(url, init, config).then((res) => {
      return res.json();
    });
  }
  /**
   * Ermöglicht Anfragen mittels der fetch-API innerhalb des Online-Bereichs der BA unter Berücksichtigung der spezifischen (Sicherheits-) Infrastruktur.
   * @param url
   * @param init siehe RequestInit von fetch
   * @param config wenn eine gültige Konfiguration übergeben wird, wird nur diese Konfiguration verwendet
   * @throws wenn übergebene "url" gleich null ist
   * @return Zeichenkette im Body der Antwort als Promise<string>
   */
  doTextFetch(url, init, config) {
    return this.fetch(url, init, config).then((res) => res.text());
  }
  /**
   * Ermöglicht Anfragen mittels der fetch-API innerhalb des Online-Bereichs der BA unter Berücksichtigung der spezifischen (Sicherheits-) Infrastruktur.
   * @param url
   * @param init siehe RequestInit von fetch
   * @param config wenn eine gültige Konfiguration übergeben wird, wird nur diese Konfiguration verwendet
   * @throws wenn übergebene "url" gleich null ist
   * @return Antwort der Anfrage als Promise<Response>
   */
  fetch(url, init, config) {
    return this.fetchProtectedResource(url, init, config).toPromise();
  }
  /**
   * Ermöglicht Anfragen mittels der fetch-API innerhalb des Online-Bereichs der BA unter Berücksichtigung der spezifischen (Sicherheits-) Infrastruktur.
   * @param url
   * @param options siehe RequestInit von fetch
   * @param baOAuthLibConfig wenn eine gültige Konfiguration übergeben wird, wird nur diese Konfiguration verwendet
   * @throws wenn übergebene "url" gleich null ist
   * @return Antwort der Anfrage als Observable<T>
   */
  fetchProtectedResource(url, options, baOAuthLibConfig) {
    if (url == null) {
      throw new Error('URL darf nicht null sein!');
    }
    baOAuthLibConfig = baOAuthLibConfig !== null && baOAuthLibConfig !== void 0 ? baOAuthLibConfig : this.baConfig;
    oauthLogger.setLevel(baOAuthLibConfig === null || baOAuthLibConfig === void 0 ? void 0 : baOAuthLibConfig.logLevel);
    const requestOptions = createRequestOptions(url, options, baOAuthLibConfig);
    return this.service.fetchOptions(requestOptions, baOAuthLibConfig).pipe(mergeMap((init) => {
      this.addDefaultHeaders(baOAuthLibConfig === null || baOAuthLibConfig === void 0 ? void 0 : baOAuthLibConfig.defaultHeaders, init);
      return from(fetch(url, init)
        .then((response) => {
        if (baOAuthLibConfig === null || baOAuthLibConfig === void 0 ? void 0 : baOAuthLibConfig.isFeatureHandleStatusActive) {
          return handleErrorStatus(response);
        }
        return response;
      })
        .then((response) => handleCorb(response)));
    }));
  }
  addDefaultHeaders(headers, value) {
    const defaultHeaders = new Headers();
    if (headers === null || headers === void 0 ? void 0 : headers.length) {
      headers.forEach(header => defaultHeaders.append(header[0], header[1]));
    }
    const requestHeaders = new Headers(value.headers);
    defaultHeaders.forEach((header) => {
      requestHeaders.append(header, defaultHeaders.get(header));
    });
    value.headers = requestHeaders;
  }
  /**
   * Erneuert eine Session, d.h. entfernt alle Tokens aus dem Cache, welche durch die übergebene Konfiguration gecacht wurden und ruft alle Tokens ab,
   * welche durch die übergebene Konfiguration abgeleitet sind.
   * @param config
   * @param expectJwt Beeinflusst ertmal nur das Error-Logging. Bei expectJwt===true wird ein Fehler bei nicht-vorhandenem Jwt geloggt.
   * @param forceRefresh Entfernt wenn true alle Tokens, welche laut übergebender Konfiguration gecacht wurden. Default ist true
   */
  refreshSession(config, forceRefresh = true, expectJwt = true) {
    oauthLogger.setLevel(config === null || config === void 0 ? void 0 : config.logLevel);
    if (forceRefresh) {
      this.resetBy(config);
    }
    const options = createRequestOptions('https://dummy/pd/api', {
      method: 'GET',
      headers: new Headers(),
    }, config);
    return this.service.fetchOptions(options, config, expectJwt).toPromise();
  }
  /**
   * Entfernt instanzspezifisch alle Tokens, welche bei Service-Aufrufen im Cache gespeichert wurden.
   * @deprecated Bitte "resetTokens" nutzen! Aufgrund der Anfälligkeit von Seiteneffekten, Zustandsaktualisierungen bei jeder Service-Anfrage und
   * der damit verbundenen Unvorhersehbarkeit, welche Einträge genau entfernt werden, ist die Nutzung der Methode risikobehaftet.
   */
  resetTokens() {
    oauthLogger.info('Es werden alle instanzspezifische Einträge aus dem Store entfernt.');
    this.service.resetToken();
  }
  /**
   * Entfernt instanzübergreifend alle Einträge aus dem Cache. Die Nutzung dieser Methode ist nur sinnvoll,
   * wenn ein expliziter Logout-Vorgang vom Anwender durchgeführt wird.
   */
  resetAllTokens() {
    oauthLogger.warn('Es werden ALLE Einträge aus dem Store entfernt.');
    clearStore();
  }
  /**
   * Entfernt alle Tokens, welche laut der übergebenen Konfiguration gespeichert wurden.
   * @param config
   * @throws wenn übergebene Konfiguration gleich null ist
   */
  resetBy(config) {
    if (config == null) {
      throw new Error('Zum Resetten von Tokens darf die Konfiguration nicht null sein!');
    }
    this.service.resetBy(config);
  }
}
//# sourceMappingURL=protected-rest.service.js.map
